import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  Bell,
  ShieldCheck,
  Navigation,
  AlertCircle,
  ArrowLeft,
  MapPin,
  Users,
  BarChart3,
} from 'lucide-react';
import { useAuth } from '../hooks/trAuth';
import { requestWebPushPermission } from '../hooks/useWebPushRegistration';
import logger from '@/utils/logger';

const BRAND_STATS = [
  {
    icon: Bell,
    color: 'text-orange-400',
    bg: 'bg-orange-500/20',
    value: '-70%',
    label: "d'appels parents",
  },
  {
    icon: ShieldCheck,
    color: 'text-green-400',
    bg: 'bg-green-500/20',
    value: '100%',
    label: 'traçabilité GPS',
  },
  {
    icon: Navigation,
    color: 'text-indigo-400',
    bg: 'bg-indigo-500/20',
    value: '-15%',
    label: 'de retards',
  },
];

const LoginPage = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login({ email: formData.email, password: formData.password }, rememberMe);

      if (result.success) {
        if (result.require_password_change) {
          logger.debug('Changement de mot de passe requis');
          navigate('/reset-password', { state: { isFirstLogin: true } });
          return;
        }
        // Le clic sur "Se connecter" est le geste utilisateur requis par Chrome pour
        // que Notification.requestPermission() affiche réellement une popup — voir
        // useWebPushRegistration.js. Fire-and-forget : ne bloque pas la navigation.
        const subscriptionPlan = result.data?.school?.subscription_plan || result.data?.subscription_plan || 'basic';
        if (subscriptionPlan?.toLowerCase() !== 'basic') {
          requestWebPushPermission({ promptIfNeeded: true, navigate });
        }

        navigate(result.data?.role === 'superadmin' ? '/superadmin' : '/admin/tracking');
      } else {
        setError(result.error || 'Identifiants incorrects. Veuillez réessayer.');
      }
    } catch (err) {
      setError('Erreur de connexion. Veuillez réessayer.');
      logger.error('Erreur login:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* ── LEFT PANEL — brand (desktop only) ────────────────────────── */}
      <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] bg-slate-900 relative overflow-hidden flex-col justify-between p-10 xl:p-16">
        {/* Decorative orbs */}
        <div className="absolute top-[-8%] right-[-6%] w-[480px] h-[480px] bg-blue-600 rounded-full blur-[140px] opacity-20 pointer-events-none" />
        <div className="absolute bottom-[-8%] left-[-6%] w-[400px] h-[400px] bg-indigo-600 rounded-full blur-[120px] opacity-20 pointer-events-none" />

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10"
        >
          <div className="inline-flex items-center gap-3 bg-white rounded-2xl px-4 py-2.5 shadow-lg">
            <img src="/logo.png" alt="M'EyeBus Logo" className="h-9 w-auto" />
            <img src="/logo_trademark.png" alt="M'EyeBus" className="h-5 w-auto object-contain" />
          </div>
        </motion.div>

        {/* Central content */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="relative z-10 flex-1 flex flex-col justify-center py-12"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/20 border border-blue-500/30 text-blue-300 text-sm font-semibold mb-8 w-fit">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-400" />
            </span>
            TRANSPORT SCOLAIRE CONNECTÉ
          </div>

          <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-5 tracking-tight">
            Le transport scolaire,{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">
              enfin sécurisé.
            </span>
          </h1>

          <p className="text-slate-400 text-lg leading-relaxed max-w-md mb-8">
            Pilotez votre flotte, gérez vos circuits et communiquez avec les parents depuis un seul tableau de bord.
          </p>

          {/* Feature checklist */}
          <ul className="space-y-3 mb-10">
            {[
              { icon: MapPin, text: 'Suivi GPS temps réel de toute votre flotte' },
              { icon: Users, text: 'Notifications push instantanées aux parents' },
              { icon: BarChart3, text: 'Dashboard analytique & rapports de flotte' },
            ].map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg bg-blue-500/20 flex items-center justify-center shrink-0">
                  <Icon className="h-4 w-4 text-blue-400" />
                </div>
                <span className="text-slate-300 text-sm">{text}</span>
              </li>
            ))}
          </ul>

          {/* Stats grid */}
          <div className="grid grid-cols-3 gap-4">
            {BRAND_STATS.map((stat, i) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.3 + i * 0.08 }}
                  className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-4 flex items-center gap-3"
                >
                  <div className={`w-10 h-10 ${stat.bg} rounded-xl flex items-center justify-center shrink-0`}>
                    <Icon className={`h-5 w-5 ${stat.color}`} />
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg leading-none">{stat.value}</p>
                    <p className="text-slate-400 text-xs mt-0.5">{stat.label}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Slogan */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="relative z-10 mb-8"
        >
          <p className="text-2xl xl:text-3xl font-bold leading-snug tracking-tight">
            <span className="text-slate-400">Keep an </span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">Eye</span>
            <span className="text-slate-400"> on every </span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">Bus.</span>
          </p>
          <p className="text-slate-500 text-sm mt-2 tracking-widest uppercase font-medium">
            Every stop. Every child. Every time.
          </p>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="relative z-10"
        >
          <div className="inline-flex items-center bg-white rounded-xl px-3 py-2 shadow-lg">
            <img src="/logo_trademark_corp.png" alt="M'EyeBus © 2026" className="h-5 w-auto object-contain" />
          </div>
        </motion.div>
      </div>

      {/* ── RIGHT PANEL — form ──────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-h-screen lg:min-h-0">
        {/* Mobile top bar */}
        <div className="lg:hidden flex items-center justify-between px-5 pt-5 pb-2">
          <Link
            to="/"
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour
          </Link>
          <img src="/logo.png" alt="M'EyeBus" className="h-9 w-auto" />
        </div>

        {/* Form area */}
        <div className="flex-1 flex items-center justify-center bg-slate-50 px-5 py-10 lg:py-0 lg:bg-white lg:px-12 xl:px-20">
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="w-full max-w-sm"
          >
            {/* Back link — desktop */}
            <Link
              to="/"
              className="hidden lg:inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-600 transition-colors mb-10"
            >
              <ArrowLeft className="h-4 w-4" />
              Retour au site
            </Link>

            {/* Header */}
            <div className="mb-8">
              <h2 className="text-2xl xl:text-3xl font-bold text-slate-900 mb-1.5">Connexion</h2>
              <p className="text-slate-500 text-sm">Accédez à votre tableau de bord administrateur</p>
            </div>

            {/* Error banner */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 text-sm"
                role="alert"
                aria-live="polite"
              >
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{error}</span>
              </motion.div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
                  Adresse email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="block w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow shadow-sm hover:border-slate-300"
                    placeholder="admin@ecole.ma"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                    Mot de passe
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                  >
                    Mot de passe oublié ?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    className="block w-full pl-10 pr-12 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow shadow-sm hover:border-slate-300"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                    aria-label={showPassword ? 'Masquer' : 'Afficher'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Remember me */}
              <div className="flex items-center gap-2.5">
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 accent-[#0B2340] cursor-pointer"
                />
                <label htmlFor="rememberMe" className="text-sm text-slate-600 cursor-pointer select-none">
                  Se souvenir de moi
                </label>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl bg-[#0B2340] hover:bg-[#0d2b4e] text-white text-sm font-bold transition-all shadow-lg shadow-slate-900/20 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-900 mt-2"
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Connexion en cours…
                  </>
                ) : (
                  'Se connecter'
                )}
              </button>
            </form>

            {/* Footer trademark */}
            <div className="mt-10 flex flex-col items-center gap-2">
              <img
                src="/logo_trademark_corp.png"
                alt="M'EyeBus © 2026"
                className="h-5 w-auto object-contain opacity-50"
              />
              <p className="text-xs text-slate-400 text-center">
                Dashboard réservé aux administrateurs d'établissements
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
